import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ShieldCheck, Terminal, Radar, Target, Bug, FileText, ArrowRight, Activity, Fingerprint, Lock, Cpu, GitBranch, Globe } from "lucide-react";

const tools = [
  { name: "nmap",         desc: "Découverte réseau & scan de ports défensif" },
  { name: "nuclei",       desc: "Détection de CVE par templates à grande échelle" },
  { name: "nikto",        desc: "Audit de configuration des serveurs web" },
  { name: "whatweb",      desc: "Empreinte des technologies exposées" },
  { name: "searchsploit", desc: "Corrélation locale avec l'Exploit-DB (CVE connues)" },
  { name: "httpx",        desc: "Sonde HTTP : statut, titres, technologies" },
  { name: "subfinder",    desc: "Inventaire des sous-domaines de ton périmètre" },
  { name: "CVE mapping",  desc: "Regroupement par hôte & sévérité en temps réel" },
];

const pipeline = [
  { step: "01", title: "Inventaire",   desc: "Cartographie du périmètre autorisé : hôtes vivants, ports, sous-domaines.", icon: Radar },
  { step: "02", title: "Empreinte",    desc: "Identification des services, versions et technologies web exposées.", icon: Fingerprint },
  { step: "03", title: "Détection",    desc: "Corrélation CVE, mauvaises configurations et failles via Nuclei + Nikto.", icon: Bug },
  { step: "04", title: "Priorisation", desc: "Classement par sévérité (CVSS) et liens Exploit-DB pour évaluer le risque.", icon: Target },
  { step: "05", title: "Remédiation",  desc: "Rapports exportables (CSV/PDF) et plan de correction par hôte.", icon: FileText },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground dark">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-gradient-danger flex items-center justify-center border border-primary/40">
              <ShieldCheck className="h-5 w-5 text-primary-foreground" strokeWidth={2.5}/>
            </div>
            <div>
              <div className="font-mono text-[10px] text-muted-foreground tracking-widest">BLUE-TEAM PLATFORM</div>
              <div className="font-bold tracking-tight leading-none">CRIMSON<span className="text-primary">.OPS</span></div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#arsenal"  className="hover:text-foreground transition-colors">Outils</a>
            <a href="#pipeline" className="hover:text-foreground transition-colors">Méthode</a>
            <a href="#stack"    className="hover:text-foreground transition-colors">Stack</a>
            <a href="#ethics"   className="hover:text-foreground transition-colors">Cadre d'usage</a>
          </nav>
          <Link to="/dashboard">
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs tracking-wider gap-2" data-testid="landing-console-btn">
              OUVRIR LA CONSOLE <ArrowRight className="h-3.5 w-3.5"/>
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-hero noise-overlay">
        <div className="absolute inset-0 grid-hex-bg opacity-40" />
        <div className="absolute top-1/2 -translate-y-1/2 -left-40 w-96 h-96 rounded-full bg-primary/20 blur-[120px]" />
        <div className="absolute top-1/4 right-0 w-96 h-96 rounded-full bg-accent/10 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-7 animate-fade-in">
            <Badge variant="outline" className="border-primary/40 bg-primary/5 text-primary font-mono text-[10px] tracking-widest gap-2 py-1.5 px-3">
              <span className="h-1.5 w-1.5 rounded-full bg-primary pulse-dot"/>
              DEFENSIVE SECURITY · v2.4.0 · CIBLES AUTORISÉES UNIQUEMENT
            </Badge>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02]">
              Trouve tes failles.<br/>
              <span className="text-gradient-danger">Avant</span> les attaquants.
            </h1>

            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Crimson.OPS orchestre <span className="text-foreground">nmap</span>, <span className="text-foreground">nuclei</span>, <span className="text-foreground">nikto</span>, <span className="text-foreground">searchsploit</span> et d'autres scanners en une plateforme défensive unifiée.
              Analyse de vulnérabilités, corrélation CVE en temps réel et rapports de remédiation — sur ton propre périmètre.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Link to="/scanner">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono tracking-wider gap-2 shadow-red-glow" data-testid="landing-scan-btn">
                  <Radar className="h-4 w-4"/> LANCER UNE ANALYSE
                </Button>
              </Link>
              <Link to="/terminal">
                <Button size="lg" variant="outline" className="border-border bg-card/40 font-mono tracking-wider gap-2">
                  <Terminal className="h-4 w-4"/> TERMINAL EN DIRECT
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap gap-6 pt-4 text-sm text-muted-foreground font-mono">
              <div><span className="text-primary font-bold">217k+</span> CVE indexées</div>
              <div><span className="text-primary font-bold">48k+</span> exploits référencés</div>
              <div><span className="text-primary font-bold">7</span> scanners intégrés</div>
            </div>
          </div>

          {/* Terminal card */}
          <div className="lg:col-span-5 animate-fade-in">
            <Card className="bg-terminal border-border shadow-red-glow relative overflow-hidden scanline">
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border bg-card/60">
                <span className="h-2.5 w-2.5 rounded-full bg-primary/70"/>
                <span className="h-2.5 w-2.5 rounded-full bg-warning/70"/>
                <span className="h-2.5 w-2.5 rounded-full bg-success/70"/>
                <span className="ml-3 font-mono text-[11px] text-muted-foreground">analyst@crimson-ops — zsh</span>
              </div>
              <div className="p-5 font-mono text-[12.5px] leading-relaxed space-y-1">
                <div><span className="text-primary">❯</span> <span className="text-terminal">crimson</span> scan --target 192.168.1.0/24 --profile standard</div>
                <div className="text-muted-foreground">[*] Outils : nmap, nuclei, nikto, whatweb, searchsploit</div>
                <div className="text-muted-foreground">[*] Périmètre autorisé résolu — 6 hôtes actifs</div>
                <div className="text-info">[+] 192.168.1.10  web-01   Apache 2.4.49  — 4 ports</div>
                <div className="text-info">[+] 192.168.1.22  db-01    MySQL 8.0      — 2 ports</div>
                <div className="text-info">[+] 192.168.1.30  nas-01   OpenSSH 8.2    — 3 ports</div>
                <div className="text-warning">[!] CVE-2021-41773  Path Traversal  — web-01 (CVSS 7.5)</div>
                <div className="text-warning">[!] TLS obsolète (TLS 1.0)         — db-01</div>
                <div className="text-success">[+] Exploit-DB : 2 correctifs recommandés</div>
                <div className="text-success">[+] Rapport PDF généré — 3 vulnérabilités priorisées</div>
                <div className="text-terminal terminal-caret"><span className="text-primary">❯</span> </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Arsenal */}
      <section id="arsenal" className="relative py-24 border-t border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="font-mono text-xs text-primary tracking-widest mb-2">// OUTILS</div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight">7 scanners. 1 interface.</h2>
              <p className="text-muted-foreground mt-3 max-w-2xl">Chaque scanner est orchestré, parallélisé et corrélé pour une visibilité défensive complète de ton périmètre.</p>
            </div>
            <Badge variant="outline" className="border-border font-mono text-[10px] tracking-widest gap-2">
              <Cpu className="h-3.5 w-3.5 text-primary"/> MOTEURS PRÊTS
            </Badge>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {tools.map((t) => (
              <Card key={t.name} className="group relative bg-card/50 border-border p-5 hover:border-primary/50 hover:bg-card transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-lg font-bold text-foreground group-hover:text-primary transition-colors">{t.name}</span>
                  <span className="h-2 w-2 rounded-full bg-success/70"/>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{t.desc}</p>
                <div className="mt-4 pt-3 border-t border-border font-mono text-[10px] text-muted-foreground tracking-widest">STATUT : PRÊT</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pipeline */}
      <section id="pipeline" className="relative py-24 border-t border-border bg-card/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="font-mono text-xs text-primary tracking-widest mb-2">// MÉTHODE DÉFENSIVE</div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">De l'inventaire à la remédiation.</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">Un pipeline d'évaluation complet. Chaque étape alimente la suivante avec une traçabilité totale.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {pipeline.map((p, i) => {
              const Icon = p.icon;
              return (
                <Card key={p.step} className="relative bg-card/60 border-border p-6 group hover:border-primary/40 transition-colors flex flex-col">
                  <div className="font-mono text-5xl font-bold text-primary/15 group-hover:text-primary/25 transition-colors leading-none">{p.step}</div>
                  <Icon className="h-5 w-5 text-primary mt-4"/>
                  <h3 className="font-bold text-lg mt-2">{p.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">{p.desc}</p>
                  {i < pipeline.length - 1 && (
                    <div className="hidden lg:flex absolute top-1/2 -right-3 h-6 w-6 items-center justify-center bg-background border border-border rounded-full z-10">
                      <ArrowRight className="h-3 w-3 text-primary"/>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats / stack */}
      <section id="stack" className="relative py-24 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="font-mono text-xs text-primary tracking-widest mb-2">// STACK</div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Conçu pour les défenseurs.<br/>Propulsé par <span className="text-primary">Python</span>.</h2>
            <p className="text-muted-foreground mt-4 leading-relaxed max-w-lg">
              Un backend Python asynchrone orchestre tous les scanners via des workers dédiés. Les résultats sont normalisés, dédupliqués et corrélés en temps réel.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mt-8">
              {[
                { icon: Activity,   t: "Flux en direct",   d: "stdout streamé vers l'UI" },
                { icon: GitBranch,  t: "Sync Exploit-DB",  d: "Corrélation CVE temps réel" },
                { icon: Globe,      t: "Profils de scan",  d: "Stealth / standard / approfondi" },
                { icon: Lock,       t: "Liste blanche",    d: "Cibles autorisées imposées" },
              ].map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.t} className="flex gap-3 p-4 rounded-md border border-border bg-card/40">
                    <Icon className="h-5 w-5 text-primary shrink-0 mt-0.5"/>
                    <div>
                      <div className="font-semibold text-sm">{f.t}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{f.d}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Card className="bg-terminal border-border p-0 overflow-hidden shadow-red-glow">
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border bg-card/60">
              <span className="h-2 w-2 rounded-full bg-primary/70"/>
              <span className="h-2 w-2 rounded-full bg-warning/70"/>
              <span className="h-2 w-2 rounded-full bg-success/70"/>
              <span className="ml-3 font-mono text-[11px] text-muted-foreground">crimson_orchestrator.py</span>
            </div>
            <pre className="p-5 font-mono text-[12px] leading-relaxed text-terminal overflow-x-auto">
{`from crimson.core import Assessment
from crimson.tools import nmap, nuclei, nikto, searchsploit

op = Assessment(
    codename="BLUEWATCH",
    targets="192.168.1.0/24",   # périmètre autorisé
    profile="standard",
)

# Phase 1 - inventaire
hosts = op.run(nmap, flags="-sV --script vulners")

# Phase 2 - détection web
op.run(nuclei.scan(hosts))
op.run(nikto.scan(hosts))

# Phase 3 - corrélation CVE
findings = op.run(searchsploit.correlate(hosts))

# Phase 4 - rapport de remédiation
op.export(findings, formats=["csv", "pdf"])
`}
            </pre>
          </Card>
        </div>
      </section>

      {/* Ethics banner */}
      <section id="ethics" className="relative py-16 border-t border-border bg-primary/5">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="h-12 w-12 shrink-0 rounded-md border border-primary/40 bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-primary"/>
          </div>
          <div className="flex-1">
            <div className="font-mono text-xs text-primary tracking-widest mb-1">// CADRE D'USAGE</div>
            <h3 className="text-xl font-bold">Analyse défensive de tes propres systèmes uniquement.</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              Crimson.OPS est un outil défensif (blue-team) réservé aux systèmes que tu possèdes ou pour lesquels tu disposes d'une autorisation écrite. La plateforme impose une liste blanche de cibles (réseaux privés, hôtes éducatifs comme scanme.nmap.org). Toute analyse d'un système tiers sans consentement est interdite.
            </p>
          </div>
          <Link to="/dashboard">
            <Button variant="outline" className="border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground font-mono text-xs tracking-wider gap-2" data-testid="landing-acknowledge-btn">
              J'AI COMPRIS <ArrowRight className="h-3.5 w-3.5"/>
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary"/>
            <span className="font-mono">crimson.ops · v2.4.0 · scanner défensif</span>
          </div>
          <div className="font-mono text-xs">© 2026 CRIMSON.OPS — usage responsable.</div>
        </div>
      </footer>
    </div>
  );
}
